import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { requestKey } from "../../hooks/useFolderOrganize";
import { awaitingReviewKey } from "../../hooks/useOrganizeAwaitingReview";
import type { FolderOrganizeRequest } from "../../types/organize";

/**
 * App-level watcher for the current user's AI organise runs (WHA-3654).
 *
 * Mounted once in App.tsx so it survives navigation: one realtime channel on
 * folder_organize_requests filtered to the user's own requests. When a run
 * reaches proposal_ready it fires the bottom-right success
 * toast, and every event feeds the per-folder react-query cache and the
 * Folders-nav awaiting-review count, keeping dialogs, pills, and the red
 * dot live on whatever page the user is on.
 */
export function OrganizeWatcher() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useAuth();
  const { currentOrganization, isReady } = useOrganizationContext();

  const orgId = isReady ? (currentOrganization?.id ?? null) : null;
  const profileId = userProfile?.id ?? null;

  // The toast text must be fresh inside a long-lived subscription callback
  // without resubscribing on every language switch.
  const readyToastRef = useRef(t.documents.organize.readyToast);
  readyToastRef.current = t.documents.organize.readyToast;
  const reviewActionRef = useRef(t.documents.organize.readyToastAction);
  reviewActionRef.current = t.documents.organize.readyToastAction;

  // Same reason as the strings: the callback is captured once by a long-lived
  // subscription, so it must read the CURRENT navigate rather than the one that
  // existed when the channel was opened.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // payload.old only carries the primary key unless the table has
  // REPLICA IDENTITY FULL, so old.status can't detect the transition into
  // proposal_ready — later writes to a proposal_ready row (cost/usage
  // backfill) would re-toast. Track toasted request ids instead.
  const toastedRef = useRef(new Set<number>());

  useEffect(() => {
    if (orgId === null || profileId === null) return;

    const channel = supabase
      .channel(`folder_organize_user_${profileId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "folder_organize_requests",
          // Realtime allows one server-side filter; scope delivery to the
          // user's OWN rows so teammates' proposals/instructions are never
          // sent to this client. RLS on the table remains the hard gate —
          // this filter is defence in depth, not a substitute.
          filter: `requested_by=eq.${profileId}`,
        },
        (payload) => {
          const row = payload.new as FolderOrganizeRequest;
          // Cross-org noise (user belongs to several orgs): ignore rows
          // outside the org currently in context.
          if (row.org_id !== orgId) return;

          queryClient.setQueryData(requestKey(row.project_id, row.folder_id), row);
          queryClient.invalidateQueries({
            queryKey: awaitingReviewKey(row.building_id, profileId),
          });

          if (row.status === "proposal_ready" && !toastedRef.current.has(row.id)) {
            toastedRef.current.add(row.id);
            toast.success(readyToastRef.current, {
              action: {
                label: reviewActionRef.current,
                onClick: () => {
                  // The LEGACY param pair on purpose: the Files page migrates
                  // `?projectId=&folderId=` onto the global `?project=` scope
                  // and opens the Folders tab at that folder. The plain scope
                  // param alone cannot open a specific folder — the same reason
                  // BreadcrumbTooltipContent builds its links this way.
                  const params = new URLSearchParams({ projectId: String(row.project_id) });
                  // A project-root run has no folder to open; the project's own
                  // Folders view is where its proposal is reviewed.
                  if (row.folder_id !== null) params.set("folderId", String(row.folder_id));
                  navigateRef.current(
                    `/buildings/${row.building_id}/documents?${params.toString()}`
                  );
                },
              },
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [orgId, profileId, queryClient]);

  return null;
}
